class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  openPending(triggeredBy) {
    this._wasEmpty = this.classList.contains('is-empty');
    this.classList.add('cart-drawer--updating');
    this.classList.remove('is-empty');
    this.open(triggeredBy);
  }

  clearPending(revertEmpty) {
    this.classList.remove('cart-drawer--updating');
    if (revertEmpty && this._wasEmpty) {
      this.classList.add('is-empty');
      this.close();
    }
    this._wasEmpty = false;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.setupTotalsObserver();
      window.restoreBundlexWidget?.();
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.updateCartUpsellToggleState();
        cartDrawerItems.updateCartUpsellVisibility();
      }

    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    window.parkBundlexWidget?.();
    this.clearPending();
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    let drawerDocument;
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;
      const sectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionContent = sectionDocument.querySelector(section.selector || '.shopify-section');
      if (!sectionElement || !sectionContent) return;
      // Adopt the parsed nodes instead of serializing and parsing the same HTML again.
      sectionElement.replaceChildren(...sectionContent.childNodes);
      if (section.id === 'cart-drawer') drawerDocument = sectionDocument;
    });
    window.refreshCartDrawerUpsells?.(drawerDocument);
    window.restoreBundlexWidget?.();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  connectedCallback() {
    this.setupTotalsObserver();
  }

  deduplicateTotals() {
    const totalsValues = document.querySelectorAll('.totals__total-value');
    totalsValues.forEach((el) => {
      // 1. Hide duplicate/injected child elements
      const children = Array.from(el.children).filter((c) => !c.classList.contains('loading__spinner'));
      if (children.length > 1) {
        const firstText = children[0].textContent.trim();
        for (let i = 1; i < children.length; i++) {
          if (children[i].textContent.trim() === firstText || children[i].classList.contains('money')) {
            children[i].style.setProperty('display', 'none', 'important');
          }
        }
      }

      // 2. Fix duplicated text inside element or text node (e.g. "$90.00 USD$90.00 USD")
      const rawText = el.textContent.trim();
      if (rawText.length > 4) {
        let singlePrice = null;
        const dupMatch = rawText.match(/^([^\d\s]*\s*[\d,.]+(?:\s+[A-Z]{3})?)\s*\1$/i);
        if (dupMatch && dupMatch[1]) {
          singlePrice = dupMatch[1];
        } else {
          const half = Math.floor(rawText.length / 2);
          const firstHalf = rawText.substring(0, half).trim();
          const secondHalf = rawText.substring(half).trim();
          if (firstHalf === secondHalf && half > 2) {
            singlePrice = firstHalf;
          } else {
            const spaceHalf = Math.floor((rawText.length - 1) / 2);
            const spaceFirst = rawText.substring(0, spaceHalf).trim();
            const spaceSecond = rawText.substring(spaceHalf).trim();
            if (spaceFirst === spaceSecond && spaceHalf > 2) {
              singlePrice = spaceFirst;
            }
          }
        }

        if (singlePrice) {
          const itemSpan = el.querySelector('.totals__total-price-item');
          if (itemSpan) {
            itemSpan.textContent = singlePrice;
            Array.from(el.childNodes).forEach((node) => {
              if (node !== itemSpan) {
                if (node.nodeType === Node.TEXT_NODE) node.textContent = '';
                else if (node.style) node.style.setProperty('display', 'none', 'important');
              }
            });
          } else {
            el.textContent = singlePrice;
          }
        }
      }
    });

    // Also deduplicate item prices if duplicate text or injected child elements exist
    const itemPrices = document.querySelectorAll('.cart-drawer .cart-item__price-wrapper .price:not(:has(s))');
    itemPrices.forEach((el) => {
      const children = Array.from(el.children).filter((c) => !c.classList.contains('loading__spinner') && c.tagName !== 'S');
      if (children.length > 1) {
        const firstText = children[0].textContent.trim();
        for (let i = 1; i < children.length; i++) {
          if (children[i].textContent.trim() === firstText || children[i].classList.contains('money')) {
            children[i].style.setProperty('display', 'none', 'important');
          }
        }
      }
      const rawText = el.textContent.trim();
      if (rawText.length > 4) {
        const dupMatch = rawText.match(/^([^\d\s]*\s*[\d,.]+(?:\s+[A-Z]{3})?)\s*\1$/i);
        if (dupMatch && dupMatch[1]) {
          el.textContent = dupMatch[1];
        } else {
          const half = Math.floor(rawText.length / 2);
          const firstHalf = rawText.substring(0, half).trim();
          const secondHalf = rawText.substring(half).trim();
          if (firstHalf === secondHalf && half > 2) {
            el.textContent = firstHalf;
          }
        }
      }
    });
  }

  setupTotalsObserver() {
    this.deduplicateTotals();
    const totalsValues = document.querySelectorAll('.totals__total-value');
    totalsValues.forEach((totalsValue) => {
      if (totalsValue._totalsObserver) return;
      const observer = new MutationObserver(() => {
        observer.disconnect();
        this.deduplicateTotals();
        observer.observe(totalsValue, { childList: true, characterData: true, subtree: true });
      });
      totalsValue._totalsObserver = observer;
      observer.observe(totalsValue, { childList: true, characterData: true, subtree: true });
    });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

window.deduplicateCartTotals = function () {
  CartDrawer.prototype.deduplicateTotals();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.deduplicateCartTotals());
} else {
  window.deduplicateCartTotals();
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
